import React, { useState, useEffect, useRef } from 'react';
import { API } from 'aws-amplify';
import qs from 'querystring';
import moment from 'moment';
import merge from 'lodash/merge';
import { makeStyles, useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Header from './components/Header';
import useSnack from 'hooks/useSnack';
import Contract from './components/Contract';
import CustomerInfoForm from './components/CustomerInfoForm';
import Payment from './components/Payment';
import ProgressSteps from './components/ProgressSteps';
import Success from './components/Success';
import withConfirm from '../../hooks/withConfirm';

const useStyles = makeStyles((theme) => ({
  main: {
    [theme.breakpoints.down('md')]: {},
  },
}));

const Checkout = ({ product, history, location, confirm }) => {
  const classes = useStyles();
  const snack = useSnack();
  const [step, setStep] = useState(0);
  const [checkoutResult, setResult] = useState();
  const [values, setValues] = useState({
    parent: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      relationship: '',
    },
    student: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    timezone: '',
    tos: false,
    coupon: '',
  });
  const query = qs.parse(location.search.substr(1));
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const rootRef = useRef();

  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('boot', { app_id: 'xkovkvvi' });
    }

    return () => {
      window.Intercom('shutdown');
    };
  }, []);

  const scrollTop = () => {
    rootRef.current.scroll({
      top: 150,
      left: 0,
      behavior: 'smooth',
    });
  };

  const handleCustomerInfoSubmit = (data) => {
    setValues(merge(values, data));
  };

  const processPayment = (token) => {
    return API.post('public', '/users/checkout', {
      body: {
        ...values,
        planId: query.plan,
        token,
      },
    }).then(
      (resp) => {
        setStep(3);
        setResult(resp);
      },
      (err) => {
        if (err.response) {
          const { message } = err.response.data;
          if (message === 'Parent account exists') {
            confirm(
              () => {
                history.push('/login');
              },
              {
                title: 'Account Exists!',
                description:
                  "We've found an account with the email address used for parent. If you are already registered with us then please login to purchase.",
                confirmationText: 'Login',
              },
            );
          } else {
            snack.error(message);
          }
        } else {
          snack.error(err.message);
        }
      },
    );
  };

  const handleNext = (values) => {
    setStep((prevActiveStep) => prevActiveStep + 1);
    scrollTop();
  };

  const handleBack = () => {
    setStep((prevActiveStep) => prevActiveStep - 1);
    scrollTop();
  };

  if (!query.plan) {
    return <Alert color="error">No product selected.</Alert>;
  }

  const { hideParentDetails, hideStudentDetails } = product.config;

  return (
    <>
      <Header />
      <main className={classes.main} ref={rootRef}>
        <Grid container>
          <Grid item xs={12} md={4}>
            <Box
              marginTop={{ xs: 2, md: 0 }}
              marginBottom={{ xs: 3, md: 0 }}
            >
              <Typography gutterBottom variant="h3">
                {product.package_name}{' '}<br/>
                {`Registration`}
                <span style={{color:theme.palette.primary.main}}>.</span>
              </Typography>
              {product.graduation_year && (
                <Typography gutterBottom variant="h5">
                  {`Class of ${product.graduation_year}`}
                </Typography>
              )}
              {product.program_start_date && (
                <Box marginTop={'18px'} display={{ xs: 'none', md: 'block' }}>
                  <Typography
                    variant="body2"
                  >
                    Start Date
                  </Typography>
                  <Typography color='primary' variant="subtitle1">
                    <em>
                      {moment.utc(product.program_start_date, 'YYYY-MM-DD').format(
                        'MMMM Do, YYYY',
                      )}
                    </em>
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <ProgressSteps
              skipParents={hideParentDetails}
              currentStep={step}
              onChangeStep={setStep}
            />

            {checkoutResult ? (
              <Success values={values} />
            ) : (
              <div>
                {step === 0 && (
                  <Box marginTop={{md: '30px'}}>
                    <Contract
                      product={product}
                      values={values}
                      setValues={setValues}
                      onNext={handleNext}
                    />
                  </Box>
                )}
                {(step === 1 || step === 2) && (
                  <Grid
                    container
                    direction="column"
                    justify="center"
                    alignItems="center"
                  >
                    <Grid item md={12}>
                      <CustomerInfoForm
                        skipParents={hideParentDetails}
                        product={product}
                        values={values}
                        onChange={setValues}
                        onSubmit={handleCustomerInfoSubmit}
                        step={step}
                        onNext={handleNext}
                        onBack={handleBack}
                      />
                    </Grid>
                  </Grid>
                )}
                {step === 3 && (
                  <Grid
                    // container
                    direction="column"
                    justify="center"
                    alignItems="center"
                  >
                    <Grid item md={12}>
                      <Payment
                        data={values}
                        onChange={setValues}
                        product={product}
                        setStep={setStep}
                        onSubmit={processPayment}
                      />
                    </Grid>
                  </Grid>
                )}
              </div>
            )}
          </Grid>
        </Grid>
      </main>
    </>
  );
};

export default withConfirm(Checkout);
